import { PushNotifications } from '@capacitor/push-notifications';
import { FCM } from '@capacitor-community/fcm';
import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import config from '../config';
import { persistentStorage } from './storage';

class PushNotificationManager {
  constructor() {
    this.isInitialized = false;
    this.registerRequested = false;
    this.token = null;
    this.notificationHandlers = new Map();
  }

  // Attach listeners once. Safe to call at app boot. Does NOT prompt for
  // permission — that happens contextually in syncTokenForUser() after login.
  async initialize() {
    if (this.isInitialized) return;
    if (!Capacitor.isNativePlatform()) return;

    try {
      PushNotifications.addListener('registration', async (token) => {
        // Android delivers an FCM registration token here. iOS delivers a raw
        // APNs token, which FCM cannot target — fetch the real FCM token from
        // the Firebase plugin instead.
        if (Capacitor.getPlatform() === 'ios') {
          try {
            const result = await FCM.getToken();
            this.token = result && result.token;
          } catch (error) {
            console.error('[push] FCM.getToken failed:', error);
            return;
          }
        } else {
          this.token = token.value;
        }
        await this.registerToken();
      });

      PushNotifications.addListener('registrationError', (error) => {
        console.error('[push] registration error:', error);
      });

      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        this.handleNotificationReceived(notification);
        // Synthetic resume signal — `useResumeSync` listens for this so every
        // dashboard's refresh callback fires when a push arrives. We skip it
        // when the WebView is genuinely foregrounded because the WebSocket
        // broadcast that fired the same push has already updated the UI in
        // real time; the refetch would be wasted work at scale.
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
          window.dispatchEvent(new Event('pariandaPushResume'));
        }
      });

      PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
        this.handleNotificationAction(notification);
        // Tap on a tray notification → app coming from background. Always
        // signal a resume; `document.visibilitychange` is unreliable on
        // push-tap on both iOS and Android, so dashboards otherwise stay
        // stale until the user fully reopens the app.
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('pariandaPushResume'));
        }
      });

      this.isInitialized = true;
    } catch (error) {
      console.error('[push] initialize failed:', error);
    }
  }

  // Call AFTER login. Requests permission contextually, registers with
  // APNs/FCM, and (re)sends the cached token now that the user is known.
  async syncTokenForUser() {
    if (!Capacitor.isNativePlatform()) return;
    if (!this.isInitialized) await this.initialize();

    // Token already in hand (e.g. registered before login completed) — just
    // make sure the backend has it tied to this user.
    if (this.token) {
      await this.registerToken();
      return;
    }

    if (this.registerRequested) return;
    this.registerRequested = true;
    try {
      const permissionStatus = await PushNotifications.requestPermissions();
      if (permissionStatus.receive !== 'granted') {
        console.warn('[push] notification permission not granted');
        return;
      }
      await PushNotifications.register();
    } catch (error) {
      console.error('[push] register failed:', error);
    }
  }

  async registerToken() {
    if (!this.token) return;
    try {
      const userId = await persistentStorage.get('currentUserId');
      const userPhone = await persistentStorage.get('currentUserPhone');

      if (!userId || !userPhone) {
        // Not logged in yet — token is cached; syncTokenForUser() retries
        // once login stores the user id.
        return;
      }

      await axios.post(`${config.API_BASE_URL}/register-fcmToken`, {
        token: this.token,
        user_id: userId,
        deviceType: Capacitor.getPlatform()
      });

      console.log('[push] FCM token registered successfully');
    } catch (error) {
      console.error('[push] token registration failed:', error);
    }
  }

  async unregister() {
    if (!Capacitor.isNativePlatform()) return;
    try {
      await PushNotifications.unregister();
      this.token = null;
      this.registerRequested = false;
      console.log('[push] unregistered');
    } catch (error) {
      console.error('[push] unregister failed:', error);
    }
  }

  // Call from every logout handler BEFORE clearing persistent storage.
  // Removes the (user_id, token) row from the backend so push notifications
  // stop reaching this device for this user. FCM itself can't know the user
  // logged out — only an app uninstall makes Google revoke the token — so
  // we have to do the mapping cleanup ourselves.
  async unregisterForUser() {
    if (!Capacitor.isNativePlatform()) return;

    // Capture identifiers BEFORE the caller wipes local storage.
    let userId = null;
    const token = this.token;
    try {
      userId = await persistentStorage.get('currentUserId');
    } catch (_) { /* non-fatal */ }

    // Tell the backend to forget the mapping. Best-effort — if the network
    // is flaky we still proceed with the local cleanup below; the server
    // also auto-prunes on UnregisteredError, so worst case the orphan row
    // sits for a few hours until the next send attempt.
    if (token || userId) {
      try {
        await axios.post(`${config.API_BASE_URL}/unregister-fcmToken`, {
          ...(userId ? { user_id: userId } : {}),
          ...(token ? { token } : {}),
        });
        console.log('[push] FCM token unregistered from backend');
      } catch (error) {
        console.error('[push] backend unregister failed:', error);
      }
    }

    // Clear OS-level registration so FCM rotates the token for the next
    // user who logs in on this device. Without this the same token would
    // be reused — still safe (the register endpoint's safety net migrates
    // it cleanly) — but a fresh token is cleaner.
    try {
      await PushNotifications.unregister();
    } catch (error) {
      console.error('[push] PushNotifications.unregister failed:', error);
    }

    // Reset in-memory state so the next syncTokenForUser starts clean.
    this.token = null;
    this.registerRequested = false;
  }

  // Register handlers for different notification types
  registerHandler(type, handler) {
    this.notificationHandlers.set(type, handler);
  }

  // Handle incoming notifications
  handleNotificationReceived(notification) {
    const { data } = notification;
    if (!data || !data.type) return;

    const handler = this.notificationHandlers.get(data.type);
    if (handler) {
      handler(notification);
    }

    switch (data.type) {
      case 'booking_update':
        this.handleBookingUpdate(data);
        break;
      case 'car_retrieval':
        this.handleCarRetrieval(data);
        break;
      case 'payment_status':
        this.handlePaymentStatus(data);
        break;
      default:
        console.log('[push] unhandled notification type:', data.type);
    }
  }

  // Handle notification clicks
  handleNotificationAction(notification) {
    const { data } = notification;
    if (!data || !data.type) return;

    switch (data.type) {
      case 'booking_update':
        window.location.href = `/parking-details/${data.bookingId}`;
        break;
      case 'car_retrieval':
        window.location.href = `/user-dashboard`;
        break;
      case 'payment_status':
        window.location.href = `/payment/${data.paymentId}`;
        break;
      default:
        console.log('[push] unhandled notification action:', data.type);
    }
  }

  handleBookingUpdate(data) {
    console.log('[push] booking update:', data);
  }

  handleCarRetrieval(data) {
    console.log('[push] car retrieval:', data);
  }

  handlePaymentStatus(data) {
    console.log('[push] payment status:', data);
  }
}

export const pushNotificationManager = new PushNotificationManager();
